import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  FaArrowLeft,
  FaDownload,
  FaPrint,
  FaCheckCircle,
  FaMotorcycle,
  FaUser,
  FaPhone,
  FaCalendarAlt,
  FaClock,
  FaMoneyBillWave,
  FaFileInvoice,
  FaHashtag,
  FaRoad,
  FaReceipt,
  FaInfoCircle,
  FaTools,
  FaPiggyBank
} from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { invoiceAPI } from '../../../utils/apiClient';
import bookingService from '../../../services/bookingService';

const UserInvoice = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [invoice, setInvoice] = useState(null);
  const [booking, setBooking] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchInvoiceAndBooking();
    }
  }, [bookingId]);

  const fetchInvoiceAndBooking = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('📄 Fetching invoice and booking for:', bookingId);
      const [invoiceResponse, bookingResponse] = await Promise.all([
        invoiceAPI.getByBookingId(bookingId),
        bookingService.getBookingById(bookingId)
      ]);
      const formattedInvoice = invoiceAPI.formatInvoiceData(invoiceResponse.data);
      const bookingData = bookingResponse.data || bookingResponse;

      setInvoice(formattedInvoice);
      setBooking(bookingData);
      console.log('✅ Invoice loaded:', formattedInvoice);
      console.log('✅ Booking loaded:', bookingData);
    } catch (error) {
      console.error('❌ Error fetching data:', error);
      setError(error.message || 'Failed to load invoice');
      toast.error(error.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    try {
      window.print();
    } catch (error) {
      console.error('Print failed:', error);
      toast.error('Failed to print invoice');
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await invoiceAPI.downloadInvoice(bookingId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Vegatas-Invoice-${invoice.invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded successfully');
    } catch (error) {
      console.error('❌ Download failed:', error);
      handlePrint();
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Invoice - ${invoice.invoiceNumber}`,
          text: `Vegatas Invoice for Booking #${invoice.bookingId}`,
          url: window.location.href
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Invoice link copied to clipboard!');
      }
    } catch (error) {
      console.error('❌ Share failed:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount) => {
    const roundedAmount = Math.round(amount || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(roundedAmount);
  };

  // Parse additional charges - separate base and GST
  const parseAdditionalCharges = (detailsString) => {
    if (!detailsString) return { extensions: [], manualCharges: [] };
    try {
      const entries = detailsString.split(' | ').filter(entry => entry.trim());
      const extensions = [];
      const manualCharges = [];
      entries.forEach((entry, index) => {
        const extMatch = entry.match(/Extend Trip (.+?) -> (.+?): base=([\d.]+), gst\(5%\)=([\d.]+), total=([\d.]+)/);
        if (extMatch) {
          extensions.push({
            id: index,
            fromDate: extMatch[1].trim(),
            toDate: extMatch[2].trim(),
            baseAmount: parseFloat(extMatch[3]),
            gstAmount: parseFloat(extMatch[4]),
            totalAmount: parseFloat(extMatch[5])
          });
        } else {
          const manualMatch = entry.match(/Manual charges: (.+)/);
          if (manualMatch) {
            const chargesStr = manualMatch[1];
            const chargeItems = chargesStr.split(',').map(item => item.trim());
            chargeItems.forEach(item => {
              const [name, amount] = item.split('=');
              if (name && amount && !name.toLowerCase().startsWith('total')) {
                manualCharges.push({
                  name: name.trim(),
                  amount: parseFloat(amount)
                });
              }
            });
          }
        }
      });
      return { extensions, manualCharges };
    } catch (error) {
      console.error('Error parsing additional charges:', error);
      return { extensions: [], manualCharges: [] };
    }
  };

  // Calculate all amounts
  const calculateInvoiceAmounts = () => {
    // Parse additional charges
    const { extensions, manualCharges } = parseAdditionalCharges(booking?.additionalChargesDetails);

    // Base amounts
    const baseRentalPrice = Math.round(booking?.charges ?? 0);
    const baseRentalGst = Math.round(booking?.gst ?? 0);
    const deliveryCharges = Math.round(invoice?.deliveryCharges || booking?.deliveryCharges || 0);
    const couponDiscount = Math.round(invoice?.couponAmount || booking?.couponAmount || 0);
    const lateFee = Math.round(invoice?.lateFeeCharges || booking?.lateFeeCharges || 0);
    const extraKmCharges = Math.round(invoice?.lateChargesKm || booking?.lateChargesKm || 0);
    const advanceAmount = Math.round(invoice?.advanceAmount || booking?.advanceAmount || 0);

    // Extension BASE amounts only (without GST)
    const extensionBaseTotal = Math.round(extensions.reduce((sum, ext) => sum + ext.baseAmount, 0));
    const extensionGstTotal = Math.round(extensions.reduce((sum, ext) => sum + ext.gstAmount, 0));

    // Manual charges (NO GST)
    const manualChargesTotal = Math.round(manualCharges.reduce((sum, charge) => sum + charge.amount, 0));

    // Subtotal before GST
    const subtotalBeforeGst = baseRentalPrice + deliveryCharges + lateFee + extraKmCharges + extensionBaseTotal + manualChargesTotal - couponDiscount;

    // Total GST amount
    const totalGstAmount = baseRentalGst + extensionGstTotal;

    // Grand total (including GST and advance)
    const grandTotal = subtotalBeforeGst + totalGstAmount + advanceAmount;

    // Final amount payable (after subtracting advance)
    const finalAmountPayable = Math.max(0, grandTotal - advanceAmount);

    // Trip distance
    const tripDistance = calculateTripDistance();

    return {
      baseRentalPrice,
      baseRentalGst,
      deliveryCharges,
      couponDiscount,
      lateFee,
      extraKmCharges,
      advanceAmount,
      extensionBaseTotal,
      extensionGstTotal,
      manualChargesTotal,
      subtotalBeforeGst,
      totalGstAmount,
      grandTotal,
      finalAmountPayable,
      tripDistance,
      extensions,
      manualCharges
    };
  };

  const calculateTripDistance = () => {
    if (booking?.startTripKm && booking?.endTripKm) {
      const distance = booking.endTripKm - booking.startTripKm;
      return Math.round(distance);
    }
    return 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading invoice...</p>
        </div>
      </div>
    );
  }

  if (error || !invoice || !booking) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">❌</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Available</h2>
          <p className="text-gray-600 mb-6">
            {error?.response?.status === 404
              ? 'Invoice not found. Please complete the booking first.'
              : 'Unable to load invoice data. Please try again.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={fetchInvoiceAndBooking}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all"
            >
              🔄 Try Again
            </button>
            <button
              onClick={() => navigate('/rental/search/my-bookings')}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg text-base font-semibold transition-all"
            >
              ← Back to Bookings
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calculate all amounts
  const amounts = calculateInvoiceAmounts();

  // Debug log
  console.log('💰 SIMPLIFIED CALCULATION:', amounts);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Action Bar */}
      <div className="max-w-4xl mx-auto mb-6 print:hidden">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/rental/search/my-bookings')}
              className="flex items-center px-4 py-2 text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors font-medium"
            >
              <FaArrowLeft className="mr-2" />
              Back to Bookings
            </button>
            <div className="flex items-center space-x-3">
              {navigator.share && (
                <button
                  onClick={handleShare}
                  className="flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors shadow-md"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share
                </button>
              )}
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors shadow-md disabled:bg-gray-400"
              >
                {downloading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <FaDownload className="mr-2" />
                    Download PDF
                  </>
                )}
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors shadow-md"
              >
                <FaPrint className="mr-2" />
                Print Invoice
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Container */}
      <div id="print-area" className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Invoice Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img src="/logo-vegatas.jpeg" alt="Vegatas" className="w-12 h-12 rounded-lg object-cover mr-3 border-2 border-white/40" />
              <div>
                <h1 className="text-3xl font-bold">Vegatas Rental Invoice</h1>
                <p className="text-indigo-100">Professional Bike Rental Services</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-blue bg-opacity-20 px-4 py-2 rounded-lg inline-block">
                <p className="text-sm opacity-90">Invoice Number</p>
                <p className="text-xl font-bold">{invoice.invoiceNumber}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Badge */}
        <div className="px-8 py-4 bg-gray-50 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <FaCheckCircle className="text-green-500 text-2xl mr-2" />
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                invoice.status === 'PAID'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {invoice.status === 'PAID' ? 'Payment Completed' : booking.status || 'Payment Pending'}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Invoice Date</p>
              <p className="text-gray-900 font-semibold">{formatDate(invoice.createdAt)}</p>
            </div>
          </div>
        </div>

        {/* Customer & Booking Details */}
        <div className="p-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-8">
            {/* Customer Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaUser className="mr-2 text-indigo-600" />
                Customer Details
              </h2>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-semibold text-gray-900">{booking.customerName || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 flex items-center">
                    <FaPhone className="mr-1" /> Contact
                  </p>
                  <p className="font-semibold text-gray-900">{booking.customerNumber || 'N/A'}</p>
                </div>
                {invoice.customerId && (
                  <div>
                    <p className="text-sm text-gray-600">Customer ID</p>
                    <p className="font-semibold text-gray-900">{invoice.customerId}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Booking Information */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaFileInvoice className="mr-2 text-indigo-600" />
                Booking Details
              </h2>
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-600 flex items-center">
                    <FaHashtag className="mr-1" /> Booking ID
                  </p>
                  <p className="font-semibold text-gray-900">{booking.bookingId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 flex items-center">
                    <FaCalendarAlt className="mr-1" /> Booking Period
                  </p>
                  <p className="font-semibold text-gray-900 text-sm">
                    {formatDate(booking.startDate)}
                    <br />
                    to {formatDate(booking.endDate)}
                  </p>
                </div>
                {booking.packageName && (
                  <div>
                    <p className="text-sm text-gray-600">Selected Package</p>
                    <p className="font-semibold text-indigo-600">{booking.packageName}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Vehicle Details */}
          <div className="bg-indigo-50 p-6 rounded-lg mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FaMotorcycle className="mr-2 text-indigo-600" />
              Vehicle Information
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Registration Number</p>
                <p className="font-bold text-gray-900 text-lg">
                  {booking.bikeDetails?.registrationNumber || 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Chassis Number</p>
                <p className="font-semibold text-gray-900">
                  {booking.bikeDetails?.chassisNumber || 'N/A'}
                </p>
              </div>
              {amounts.tripDistance > 0 && (
                <div>
                  <p className="text-sm text-gray-600 flex items-center">
                    <FaRoad className="mr-1" /> Trip Distance
                  </p>
                  <p className="font-bold text-gray-900">{amounts.tripDistance} km</p>
                </div>
              )}
            </div>
            {booking.startTripKm && booking.endTripKm && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-indigo-200">
                <div>
                  <p className="text-sm text-gray-600">Start KM</p>
                  <p className="font-semibold text-gray-900">{Math.round(booking.startTripKm)} km</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">End KM</p>
                  <p className="font-semibold text-gray-900">{Math.round(booking.endTripKm)} km</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Total KM Traveled</p>
                  <p className="font-semibold text-gray-900">{amounts.tripDistance} km</p>
                </div>
              </div>
            )}
          </div>

          {/* Charges Breakdown */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <FaMoneyBillWave className="mr-2 text-green-600" />
              Charges Breakdown
            </h2>
            <div className="space-y-3">
              {/* Base Rental - WITHOUT GST */}
              <div className="flex justify-between items-center py-2 text-sm sm:text-base">
                <span className="text-gray-700">Base Rental Charge</span>
                <span className="font-semibold whitespace-nowrap">{formatCurrency(amounts.baseRentalPrice)}</span>
              </div>

              {/* Delivery */}
              {amounts.deliveryCharges > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base border-t border-gray-100">
                  <span className="text-gray-700">Delivery Charges</span>
                  <span className="font-semibold whitespace-nowrap">{formatCurrency(amounts.deliveryCharges)}</span>
                </div>
              )}

              {/* Late Fee */}
              {amounts.lateFee > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base border-t border-gray-100">
                  <span className="text-orange-600 flex items-center">
                    <FaClock className="mr-1 flex-shrink-0 text-xs" />
                    Late Return Fee
                  </span>
                  <span className="font-semibold text-orange-600 whitespace-nowrap">{formatCurrency(amounts.lateFee)}</span>
                </div>
              )}

              {/* Extra KM */}
              {amounts.extraKmCharges > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base border-t border-gray-100">
                  <span className="text-orange-600 flex items-center">
                    <FaRoad className="mr-1 flex-shrink-0 text-xs" />
                    Extra KM Charges
                  </span>
                  <span className="font-semibold text-orange-600 whitespace-nowrap">{formatCurrency(amounts.extraKmCharges)}</span>
                </div>
              )}

              {/* Trip Extensions - BASE ONLY (NO GST) */}
              {amounts.extensions.length > 0 && (
                <div className="border-t border-gray-100 pt-2">
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base">
                    <span className="text-purple-700 font-medium">Trip Extensions ({amounts.extensions.length})</span>
                    <span className="font-semibold text-purple-700 whitespace-nowrap">{formatCurrency(amounts.extensionBaseTotal)}</span>
                  </div>
                  <div className="ml-4 sm:ml-6 mt-1 space-y-1">
                    {amounts.extensions.map((ext, index) => (
                      <div key={ext.id} className="text-xs sm:text-sm text-gray-600 py-1">
                        <div className="flex justify-between">
                          <span>#{index + 1}: {formatDate(ext.fromDate).split(',')[0]} → {formatDate(ext.toDate).split(',')[0]}</span>
                          <span className="font-medium ml-2">{formatCurrency(ext.baseAmount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Manual Charges (NO GST) */}
              {amounts.manualCharges.length > 0 && (
                <div className="border-t border-gray-100 pt-2">
                  <div className="flex justify-between items-center py-2 text-sm sm:text-base">
                    <span className="text-red-700 font-medium flex items-center">
                      <FaTools className="mr-1 flex-shrink-0 text-xs" />
                      Other Charges ({amounts.manualCharges.length})
                    </span>
                    <span className="font-semibold text-red-700 whitespace-nowrap">{formatCurrency(amounts.manualChargesTotal)}</span>
                  </div>
                  <div className="ml-4 sm:ml-6 mt-1 space-y-1">
                    {amounts.manualCharges.map((charge, index) => (
                      <div key={index} className="text-xs sm:text-sm text-gray-600 py-1">
                        <div className="flex justify-between">
                          <span>{charge.name}</span>
                          <span className="font-medium ml-2">{formatCurrency(charge.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coupon Discount */}
              {amounts.couponDiscount > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base border-t border-gray-100 bg-green-50 -mx-6 px-6">
                  <span className="text-green-700 font-medium">🎉 Coupon Discount</span>
                  <span className="font-semibold text-green-700 whitespace-nowrap">- {formatCurrency(amounts.couponDiscount)}</span>
                </div>
              )}

              {/* Subtotal Before GST */}
              <div className="flex justify-between items-center py-3 text-sm sm:text-base border-t-2 border-gray-300 bg-gray-50 -mx-6 px-6">
                <span className="font-bold text-gray-900">Subtotal (Before GST)</span>
                <span className="font-bold text-gray-900 text-base sm:text-lg whitespace-nowrap">{formatCurrency(amounts.subtotalBeforeGst)}</span>
              </div>

              {/* GST - SINGLE ROW */}
              <div className="flex justify-between items-center py-2 text-sm sm:text-base bg-yellow-50 -mx-6 px-6 border-t border-gray-200">
                <div className="flex flex-col">
                  <span className="text-gray-900 font-bold">GST @ 5%</span>
                  <span className="text-xs text-gray-600">
                    (Base: {formatCurrency(amounts.baseRentalGst)} + Extensions: {formatCurrency(amounts.extensionGstTotal)})
                  </span>
                </div>
                <span className="font-bold text-gray-900 whitespace-nowrap">+ {formatCurrency(amounts.totalGstAmount)}</span>
              </div>

              {/* Advance Amount Received */}
              {amounts.advanceAmount > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base bg-blue-50 -mx-6 px-6">
                  <span className="text-blue-900 font-medium flex items-center">
                    <FaPiggyBank className="mr-1 flex-shrink-0 text-xs" />
                    Advance Amount Received
                  </span>
                  <span className="font-semibold text-blue-700 whitespace-nowrap">+ {formatCurrency(amounts.advanceAmount)}</span>
                </div>
              )}

              {/* Grand Total */}
              <div className="flex justify-between items-center py-3 text-base sm:text-lg border-t-2 border-indigo-300 bg-indigo-50 -mx-6 px-6">
                <span className="font-bold text-indigo-900">Grand Total (incl. Advance)</span>
                <span className="font-bold text-indigo-700 text-lg sm:text-xl whitespace-nowrap">{formatCurrency(amounts.grandTotal)}</span>
              </div>

              {/* Advance Adjustment */}
              {amounts.advanceAmount > 0 && (
                <div className="flex justify-between items-center py-2 text-sm sm:text-base bg-blue-50 -mx-6 px-6">
                  <span className="text-blue-900 font-medium">(-) Advance Amount Adjustment</span>
                  <span className="font-semibold text-blue-700 whitespace-nowrap">- {formatCurrency(amounts.advanceAmount)}</span>
                </div>
              )}

              {/* Final Payable */}
              <div className="flex justify-between items-center py-4 text-lg sm:text-xl bg-gradient-to-r from-green-100 to-emerald-100 -mx-6 px-6 rounded-b-lg border-t-2 border-green-400">
                <div>
                  <span className="font-bold text-green-900 block">Amount to Pay</span>
                  <span className="text-xs sm:text-sm text-green-700">{amounts.finalAmountPayable === 0 ? 'Fully Paid ✓' : 'Balance Due'}</span>
                </div>
                <span className="font-black text-green-700 text-xl sm:text-2xl md:text-3xl whitespace-nowrap">{formatCurrency(amounts.finalAmountPayable)}</span>
              </div>
            </div>
          </div>

          {/* Payment Method & Address */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Payment Method</p>
              <p className="font-semibold text-gray-900">
                {booking.paymentType === 2 ? 'Online Payment' : 'Cash'}
              </p>
            </div>
            {booking.addressType && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Address Type</p>
                <p className="font-semibold text-gray-900">{booking.addressType}</p>
              </div>
            )}
          </div>

          {booking.address && (
            <div className="mt-4 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Pickup/Delivery Address</p>
              <p className="text-gray-900">{booking.address}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 border-t">
          <div className="text-center text-sm text-gray-600">
            <p className="mb-2">Thank you for choosing Vegatas! 🚲</p>
            <p>We hope you had a great ride experience with us.</p>
            <div className="mt-4 flex flex-wrap justify-center gap-4 text-xs text-gray-500">
              <span>📧 Vegatasbike00@gmail.com</span>
              <span>📞 +91 9921426002</span>
            </div>
            <p className="mt-4 text-xs text-gray-500">
              This is a computer-generated invoice and does not require a signature.
            </p>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print\\:hidden {
            display: none !important;
          }
          #print-area,
          #print-area * {
            visibility: visible;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
        }
        @page {
          margin: 0.5cm;
          size: A4;
        }
      `}</style>
    </div>
  );
};

export default UserInvoice;
